/**
 * Postits — mural colaborativo.
 *
 * Slide do tipo mission com missionId='lab.collab.postits'. Suporta modo
 * 'free' (grid) e 'columns' (colunas com seletor). Cada aluno pode colar até
 * maxPerStudent post-its; reagir (toggle like); editar/deletar próprios;
 * master pode deletar qualquer.
 *
 * Schema do frontmatter — ver SPEC-ATLAS-V2 §2.3.
 */

import { useState } from 'react'

import { useAdapter } from '../../adapter'
import type { MissionComponentProps } from '../../games/atlas/components'
import type { PostitEntry } from '../../types/adapter'
import { usePostits } from './usePostits'

interface Column {
  id: string
  label: string
  color?: string
}

export function Postits(props: MissionComponentProps) {
  const adapter = useAdapter()
  const config = props.slideConfig ?? {}
  const slideIndex = props.slideIndex ?? 0
  const prompt = (config.prompt as string) || ''
  const mode = ((config.mode as string) || 'free') as 'free' | 'columns'
  const maxPerStudent = (config.maxPerStudent as number) || 3
  const maxLength = (config.maxLength as number) || 200
  const columns: Column[] = Array.isArray(config.columns)
    ? (config.columns as Column[])
    : []
  const anonymousDefault = Boolean(config.anonymous)
  const allowReactions = config.allowReactions !== false

  const postits = usePostits(slideIndex)
  const myId = props.membershipId
  const myPostits = myId ? postits.filter((p) => p.membershipId === myId) : []
  const canAddMore = myPostits.length < maxPerStudent

  const [text, setText] = useState('')
  const [columnId, setColumnId] = useState<string>(columns[0]?.id ?? '')

  function handleAdd() {
    if (!text.trim() || props.readOnly || !canAddMore) return
    if (mode === 'columns' && !columnId) return
    adapter.createPostit({
      slideIndex,
      text,
      columnId: mode === 'columns' ? columnId : null,
      anonymous: anonymousDefault,
    })
    setText('')
    if (props.onMissionEvent) {
      props.onMissionEvent('created', {
        column: mode === 'columns' ? columnId : null,
        anonymous: anonymousDefault,
      })
    }
  }

  function handleReact(p: PostitEntry) {
    if (!allowReactions || props.readOnly) return
    adapter.reactPostit({ postitId: p.id })
  }

  function handleDelete(p: PostitEntry) {
    const isOwner = p.membershipId === myId
    const isMaster = adapter.role === 'master'
    if (!isOwner && !isMaster) return
    adapter.deletePostit({ postitId: p.id })
  }

  function isReactedByMe(p: PostitEntry): boolean {
    return myId ? p.reactedBy.includes(myId) : false
  }

  function renderPostit(p: PostitEntry) {
    const isOwner = p.membershipId === myId
    const isMaster = adapter.role === 'master'
    return (
      <li
        key={p.id}
        style={{
          background: '#fff8b8',
          padding: 10,
          borderRadius: 4,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          listStyle: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          minHeight: 80,
        }}
      >
        <div style={{ whiteSpace: 'pre-wrap', flex: 1, fontSize: '0.9em' }}>{p.text}</div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '0.7em',
            color: '#6b6b6b',
          }}
        >
          <span>{p.anonymous ? 'anônimo' : `aluno ${p.membershipId.slice(0, 6)}`}</span>
          <span style={{ display: 'flex', gap: 6 }}>
            {allowReactions && (
              <button
                onClick={() => handleReact(p)}
                disabled={props.readOnly}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: props.readOnly ? 'default' : 'pointer',
                  padding: 2,
                  color: isReactedByMe(p) ? '#d4474a' : '#888',
                  fontSize: '0.95em',
                }}
                title={isReactedByMe(p) ? 'Remover curtida' : 'Curtir'}
              >
                ♥ {p.reactionCount}
              </button>
            )}
            {(isOwner || isMaster) && (
              <button
                onClick={() => handleDelete(p)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#888',
                  padding: 2,
                }}
                title={isMaster && !isOwner ? 'Remover (moderar)' : 'Deletar'}
              >
                ×
              </button>
            )}
          </span>
        </div>
      </li>
    )
  }

  return (
    <div
      style={{
        maxWidth: 1000,
        margin: '0 auto',
        padding: 'var(--spacing-lab-5, 24px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-lab-4, 16px)',
        minHeight: 'calc(100dvh - 200px)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-lab-xl, 1.5rem)' }}>{prompt}</h2>

      {mode === 'free' && (
        <ul
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 12,
            margin: 0,
            padding: 0,
            flex: 1,
          }}
        >
          {postits.length === 0 && (
            <li style={{ listStyle: 'none', opacity: 0.5, fontStyle: 'italic' }}>
              Ainda sem post-its. Seja o primeiro!
            </li>
          )}
          {postits.map(renderPostit)}
        </ul>
      )}

      {mode === 'columns' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.max(1, columns.length)}, 1fr)`,
            gap: 12,
            flex: 1,
            alignItems: 'flex-start',
          }}
        >
          {columns.map((col) => {
            const colPostits = postits.filter((p) => p.columnId === col.id)
            return (
              <div
                key={col.id}
                style={{
                  background: 'var(--color-lab-bg-2, #F2F2ED)',
                  borderRadius: 6,
                  padding: 8,
                  borderTop: `4px solid ${col.color || 'var(--color-lab-accent, #5B2DB8)'}`,
                  minHeight: 200,
                }}
              >
                <h3 style={{ margin: '4px 0 8px', fontSize: '0.95em' }}>
                  {col.label} <span style={{ opacity: 0.5 }}>({colPostits.length})</span>
                </h3>
                <ul
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    margin: 0,
                    padding: 0,
                  }}
                >
                  {colPostits.map(renderPostit)}
                </ul>
              </div>
            )
          })}
        </div>
      )}

      {/* Composer */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'flex-start',
          padding: 12,
          background: 'var(--color-lab-bg-2, #F2F2ED)',
          borderRadius: 6,
        }}
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, maxLength))}
          placeholder={canAddMore ? 'Escreva seu post-it…' : `Limite de ${maxPerStudent} atingido`}
          disabled={!canAddMore || props.readOnly}
          rows={2}
          style={{
            flex: 1,
            padding: 8,
            fontSize: 'var(--text-lab-base, 1rem)',
            fontFamily: 'inherit',
            border: '1px solid var(--color-lab-rule, #D8D5CB)',
            borderRadius: 4,
            resize: 'vertical',
          }}
        />
        {mode === 'columns' && (
          <select
            value={columnId}
            onChange={(e) => setColumnId(e.target.value)}
            disabled={!canAddMore || props.readOnly}
            style={{
              padding: 8,
              fontSize: 'var(--text-lab-base, 1rem)',
              border: '1px solid var(--color-lab-rule, #D8D5CB)',
              borderRadius: 4,
            }}
          >
            {columns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        )}
        <button
          onClick={handleAdd}
          disabled={!text.trim() || !canAddMore || props.readOnly}
          style={{
            padding: '8px 16px',
            background: 'var(--color-lab-accent, #5B2DB8)',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            opacity: !text.trim() || !canAddMore ? 0.5 : 1,
            fontSize: 'var(--text-lab-base, 1rem)',
          }}
        >
          Colar
        </button>
      </div>
      <div style={{ fontSize: '0.8em', opacity: 0.6 }}>
        {myPostits.length} / {maxPerStudent} post-its meus · {text.length} / {maxLength} chars
      </div>
    </div>
  )
}
